/**
 * Organization Coaching Analytics Page
 *
 * Detailed view of coaching activity and performance.
 * Route: /org/:orgSlug/analytics/coaching
 */

import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetOrgCoachingSummaryQuery } from '../store/api/orgAnalyticsApi';
import { useGetOrganizationBySlugQuery } from '../store/api/trainingApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

type TimePeriod = '7d' | '30d' | '90d' | 'all';

export function OrgCoachingAnalyticsPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');

  // Get org ID from slug
  const { data: org, isLoading: loadingOrg } = useGetOrganizationBySlugQuery(orgSlug || '', {
    skip: !orgSlug,
  });

  const since = useMemo(() => {
    if (timePeriod === 'all') return undefined;
    const days = timePeriod === '7d' ? 7 : timePeriod === '30d' ? 30 : 90;
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString();
  }, [timePeriod]);

  const {
    data: summary,
    isLoading: loadingSummary,
    isError: analyticsFailed,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useGetOrgCoachingSummaryQuery(
    { orgId: org?.id || '', since },
    { skip: !org?.id }
  );

  if (loadingOrg || loadingSummary) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-gray-900">Organization not found</h1>
      </div>
    );
  }

  // A failed load is a failure, not an organization with no activity: never
  // fall through to the dashboard, whose counters would all read 0.
  if (analyticsFailed) {
    return (
      <div className="py-16 max-w-xl mx-auto">
        <LoadErrorNotice
          error={analyticsError}
          onRetry={() => void refetchAnalytics()}
          failedMessage="This organization's coaching analytics could not be loaded."
          testIdPrefix="org-coaching-analytics"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link to={`/org/${orgSlug}/analytics`} className="text-indigo-600 hover:text-indigo-700">
          Analytics
        </Link>
        <span className="text-gray-400">/</span>
        <span className="text-gray-600">Coaching</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coaching Analytics</h1>
          <p className="mt-1 text-gray-500">
            {summary?.activeEngagements || 0} active coaching engagements
          </p>
        </div>
        <select
          value={timePeriod}
          onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Total Engagements</h3>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {summary?.totalEngagements || 0}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {summary?.activeEngagements || 0} active
          </p>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Completed Sessions</h3>
          <div className="mt-2 text-3xl font-bold text-green-600">
            {summary?.completedSessionCount || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Scheduled</h3>
          <div className="mt-2 text-3xl font-bold text-blue-600">
            {summary?.coachingSessions.scheduled || 0}
          </div>
        </div>
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-sm font-medium text-gray-500">Pending Requests</h3>
          <div className="mt-2 text-3xl font-bold text-yellow-600">
            {summary?.coachingSessions.requested || 0}
          </div>
        </div>
      </div>

      {/* Session Status Distribution */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Status</h2>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {summary?.coachingSessions.requested || 0}
            </div>
            <div className="text-sm text-gray-500">Requested</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {summary?.coachingSessions.scheduled || 0}
            </div>
            <div className="text-sm text-gray-500">Scheduled</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {summary?.coachingSessions.completed || 0}
            </div>
            <div className="text-sm text-gray-500">Completed</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {summary?.coachingSessions.cancelled || 0}
            </div>
            <div className="text-sm text-gray-500">Cancelled</div>
          </div>
        </div>
      </div>

      {/* Recent Completed Sessions */}
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Recent Completed Sessions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coach
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {summary?.recentCompletedSessions.map((session) => (
                <tr key={session.bookingId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900">{session.clientName}</span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{session.coachName}</td>
                  <td className="px-6 py-4 text-center">
                    {session.lastScoreOverall != null ? (
                      <span
                        className={`font-medium ${
                          session.lastScoreOverall >= 70
                            ? 'text-green-600'
                            : session.lastScoreOverall >= 50
                              ? 'text-yellow-600'
                              : 'text-red-600'
                        }`}
                      >
                        {session.lastScoreOverall.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-500">
                    {new Date(session.completedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {(!summary?.recentCompletedSessions || summary.recentCompletedSessions.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    No completed sessions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {summary?.totalEngagements === 0 && (
        <div className="bg-white rounded-lg border p-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Coaching Activity</h3>
          <p className="text-gray-500 mb-4">
            Start by setting up coaching engagements for your team members.
          </p>
          <Link
            to="/marketplace/experts"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700"
          >
            Find Coaches
          </Link>
        </div>
      )}
    </div>
  );
}

export default OrgCoachingAnalyticsPage;
